import React, { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import Window from "../../../elements/window";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "../../style.scss";

function ListClient(props) {
  const location = useLocation();
  const [table, setTable] = useState({
    head: ["#", "Username", "Group", "Email", "Actions"],
    data: null,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [groups, setGroups] = useState([]);
  const [load, setLoad] = useState(0); // 0: loading, 1: data loaded, 2: ready for export

  // Ref to track previous export state
  const prevExport = useRef(0);

  useEffect(() => {
    const fetchData = async () => {
      if (props.user && props.user.data) {
        setLoad(0); // Start loading
        try {
          // Fetch users
          const userResponse = await axios.get(
            props.chimeraserv.urlGet + "/user",
            {
              headers: {
                Authorization: props.user.data.usrKey,
                "Content-Type": "application/json",
                Accept: "application/json",
              },
            }
          );
          let data = [];
          if (Object.keys(userResponse.data).length) {
            data = userResponse.data;
          }
          setTable((prev) => ({
            ...prev,
            data: data,
          }));

          // Fetch groups
          const groupResponse = await axios.get(
            props.chimeraserv.urlGet + "/group",
            {
              headers: {
                Authorization: props.user.data.usrKey,
                "Content-Type": "application/json",
                Accept: "application/json",
              },
            }
          );

          setGroups(groupResponse.data || []);
          setLoad(1); // Data loaded
        } catch (error) {
          setTable((prev) => ({
            ...prev,
            data: [],
          }));
          setGroups([]);
          setLoad(1); // Even on error, mark as loaded
        }
      }
    };
    fetchData();
    // eslint-disable-next-line
  }, [props.user, props.chimeraserv.urlGet]);

  // Set load to 2 when data is ready
  useEffect(() => {
    if (load === 1 && table.data !== null) {
      setLoad(2);
    }
  }, [load, table.data]);

  // Filter and search logic using useMemo for performance
  const filteredData = useMemo(() => {
    if (!table.data) return [];

    return table.data
      .filter((data) => !(data.usrKey === props.user.data.usrKey))
      .filter((user) => {
        // Find the group for this user
        const userGroup = groups.find((g) => g._id === user.group);
        const groupName = userGroup ? userGroup.name : "";

        // Search filter - now includes group name
        const matchesSearch =
          searchTerm === "" ||
          user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          groupName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (user.type &&
            user.type.toLowerCase().includes(searchTerm.toLowerCase()));

        // Type filter
        const matchesType = filterType === "all" || user.type === filterType;

        // Status filter
        const matchesStatus =
          filterStatus === "all" ||
          (filterStatus === "active" && user.status === 1) ||
          (filterStatus === "inactive" && user.status === 0);

        return matchesSearch && matchesType && matchesStatus;
      });
  }, [
    table.data,
    searchTerm,
    filterType,
    filterStatus,
    props.user.data.usrKey,
    groups, // Add groups to dependencies since we're using it in the filter
  ]);

  // Component that receives props from Window component - handles export
  const UserListContent = ({ export: exportTime, getData }) => {
    // Export logic
    useEffect(() => {
      if (prevExport.current !== exportTime && load === 2 && getData) {
        prevExport.current = exportTime;

        // Prepare export data based on filtered results
        const exportData = filteredData.map((user, index) => {
          const userGroup = groups.find((g) => g._id === user.group);
          const groupName = userGroup ? userGroup.name : "N/A";
          const status = user.status === 1 ? "Active" : "Inactive";

          return [
            (index + 1).toString(),
            user.username,
            groupName,
            user.email,
            status,
          ];
        });

        // Get total count for summary
        const totalUsers = filteredData.length;
        const activeUsers = filteredData.filter((u) => u.status === 1).length;
        const inactiveUsers = filteredData.filter((u) => u.status === 0).length;

        // Create summary data
        const summaryData = [
          ["Total Users", totalUsers.toString()],
          ["Active Users", activeUsers.toString()],
          ["Inactive Users", inactiveUsers.toString()],
          ...(searchTerm || filterStatus !== "all"
            ? [
                ["Filtered Results", "Yes"],
                ...(searchTerm ? [["Search Term", searchTerm]] : []),
                ...(filterStatus !== "all"
                  ? [
                      [
                        "Status Filter",
                        filterStatus.charAt(0).toUpperCase() +
                          filterStatus.slice(1),
                      ],
                    ]
                  : []),
              ]
            : []),
        ];

        getData({
          type: "table",
          title: "User_Management_Summary",
          // sheetName: "All_User_List",
          body: [
            // Main user data table
            {
              head: [["#", "Username", "Group", "Email", "Status"]],
              body: exportData,
              sheetName: "Users",
            },
            // Summary table
            {
              head: [["Summary", "Count"]],
              body: summaryData,
              sheetName: "Summary",
            },
          ],
          id: "exportPdf_UserList",
        });
      }
      // eslint-disable-next-line
    }, [
      exportTime,
      load,
      filteredData,
      groups,
      searchTerm,
      filterStatus,
      getData,
    ]);

    return (
      <div className="table-container">
        <table className="table table-striped defaultTable">
          <thead>
            <tr>
              {table.head.map((head, key) => (
                <th scope="col" key={key}>
                  {head}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredData.length > 0 ? (
              renderData()
            ) : (
              <tr>
                <td
                  colSpan={table.head.length}
                  className="text-center no-results"
                >
                  {searchTerm ||
                  filterType !== "all" ||
                  filterStatus !== "all" ? (
                    <div>
                      <FontAwesomeIcon
                        icon={["fas", "search"]}
                        className="no-results-icon"
                      />
                      <p>No users found matching your search criteria</p>
                      <button className="btn btn-link" onClick={clearFilters}>
                        Clear filters to see all users
                      </button>
                    </div>
                  ) : (
                    <div>
                      <FontAwesomeIcon
                        icon={["fas", "users"]}
                        className="no-results-icon"
                      />
                      <p>No users available</p>
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const valueChange = async (data, key) => {
    const updatedStatus = data.status === 1 ? 0 : 1;
    const updatedData = { ...data, status: updatedStatus };

    // Optimistically update UI
    setTable((prev) => ({
      ...prev,
      data: prev.data.map((item) =>
        item._id === data._id ? { ...item, status: updatedStatus } : item
      ),
    }));

    try {
      await axios.put(props.chimeraserv.urlPost + "/user/update", updatedData, {
        headers: {
          Authorization: props.user.data.usrKey,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });
      // Optionally refetch or handle success
    } catch (error) {
      // Revert UI change on error
      setTable((prev) => ({
        ...prev,
        data: prev.data.map((item) =>
          item._id === data._id ? { ...item, status: data.status } : item
        ),
      }));
      console.error("Error updating user status:", error);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleTypeFilter = (e) => {
    setFilterType(e.target.value);
  };

  const handleStatusFilter = (e) => {
    setFilterStatus(e.target.value);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterType("all");
    setFilterStatus("all");
  };

  const renderData = () =>
    filteredData.map((data, key) => {
      const group = groups.find((g) => g._id === data.group);
      return (
        <tr key={data._id}>
          <td>{key + 1}</td>
          <td>
            <div className="user-info">
              <strong>{data.username}</strong>
              {/* {data.type && <span className="user-type-badge">{data.type}</span>} */}
            </div>
          </td>
          <td>{group ? group.name : "N/A"}</td>
          <td>{data.email}</td>
          {/* <td>
          <span className={`type-badge type-${data.type}`}>
            {data.type || "N/A"}
          </span>
        </td> */}
          {/* <td>
          <span
            className={`status-badge ${data.status ? "active" : "inactive"}`}
          >
            {data.status ? "Active" : "Inactive"}
          </span>
        </td> */}
          <td className="actions">
            {props.user.data.level === 0 ? (
              <div className="actions actionsAdmin">
                <label className="switch">
                  <input
                    type="checkbox"
                    id={data._id}
                    checked={!!data.status}
                    onChange={() => valueChange(data, key)}
                  />
                  <span className="slider"></span>
                </label>
                <Link
                  to={
                    location.pathname.split("/").splice(0, 3).join("/") +
                    "/edit/" +
                    data._id
                  }
                >
                  <FontAwesomeIcon className="icon" icon={["fas", "cog"]} />{" "}
                  Edit
                </Link>
              </div>
            ) : (
              <div />
            )}
            <Link
              to={
                location.pathname.split("/").splice(0, 3).join("/") +
                "/view/" +
                data._id
              }
            >
              <FontAwesomeIcon className="icon" icon={["fas", "eye"]} /> View
            </Link>
          </td>
        </tr>
      );
    });

  const Loader =
    props.components && props.components.setting && props.components.setting[1]
      ? props.components.setting[1].component
      : () => <div>Loading...</div>;

  return (
    <div className="custForm" id="exportPdf_UserList">
      <div className="viewTitle">List of Users</div>

      {/* Search and Filter Controls */}
      <div className="table-controls">
        <div className="search-section">
          <div className="search-input-group">
            <FontAwesomeIcon className="search-icon" icon={["fas", "search"]} />
            <input
              type="text"
              className="form-control search-input"
              placeholder="Search by username, email, group, or type..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            {searchTerm && (
              <button
                type="button"
                className="clear-search"
                onClick={() => setSearchTerm("")}
                title="Clear search"
              >
                <FontAwesomeIcon icon={["fas", "times"]} />
              </button>
            )}
          </div>
        </div>

        <div className="filters-section">
          {/* <select
            className="form-control filter-select"
            value={filterType}
            onChange={handleTypeFilter}
          >
            <option value="all">All Types</option>
            <option value="admin">Admin</option>
            <option value="user">User</option>
            <option value="demo">Demo</option>
          </select> */}

          <select
            className="form-control filter-select"
            value={filterStatus}
            onChange={handleStatusFilter}
          >
            <option value="all">Select Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <button
            type="button"
            className="btn btn-secondary clear-filters"
            onClick={clearFilters}
            title="Clear all filters"
          >
            <FontAwesomeIcon icon={["fas", "refresh"]} /> Clear
          </button>
        </div>
      </div>

      {/* Results Summary */}
      {table.data && (
        <div className="results-summary">
          Showing {filteredData.length} of {table.data.length - 1} users
          {(searchTerm || filterType !== "all" || filterStatus !== "all") && (
            <span className="filtered-indicator">
              <FontAwesomeIcon icon={["fas", "filter"]} /> Filtered
            </span>
          )}
        </div>
      )}

      {!table.data ? (
        <Loader />
      ) : (
        <Window
          user={props.user}
          chimeraserv={props.chimeraserv}
          title={"Users"}
        >
          <UserListContent />
        </Window>
      )}
    </div>
  );
}

export default ListClient;
